import React, { useState, useContext, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, Button, Form, Modal, Alert, Dropdown } from 'react-bootstrap';
import { Pencil, Trash, CircleFill, BellFill, ChevronDown, ChevronRight, Archive } from 'react-bootstrap-icons';
import { useQueryClient } from '@tanstack/react-query';
import { AuthContext } from '../../contexts/AuthContext';
import { AppStateContext } from '../../contexts/AppStateContext';
import { BannerContext } from '../../contexts/BannerContext';
import { TradingContext } from '../../contexts/TradingContext';
import { useCreateTradeAlert, useUpdateTradeAlert, useDeleteTradeAlert } from '../../hooks/useBackend';
import { useTradingLockoutsQuery, queryKeys } from '../../hooks/useTradeStationQueries';
import { useGetTicker } from '../../hooks/useTradeStation';
import TickerComboBox from '../shared/TickerComboBox';
import { formatStdDevLabel } from '../../lib/utils';
import { useTheme } from '../../contexts/ThemeContext';
import useIsMobile from '../../hooks/useIsMobile';
import analyticsService from '../../services/analyticsService';
import { navigateToTradeWithSymbol } from '../../utils/tradeNavigation';
import { canAccessTradeWorkspace, supportsMarketData } from '../../utils/brokerConnection';
import { getAccountDisplayLabel } from '../../utils/accountNickname';

const TRIGGERED_ALERT_COLOR = '#EAB308';

const getPriceAlertStatus = (alert) => {
  const triggered = !!alert.triggered_at;
  const acknowledged = !!(alert.acknowledged_at || alert.acknowledgedAt);
  if (triggered && acknowledged) return 'acknowledged';
  if (triggered) return 'triggered';
  if (alert.is_active) return 'active';
  return 'inactive';
};

const getPriceStatusPresentation = (status, colors) => {
  switch (status) {
    case 'triggered':
      return { label: 'Triggered', color: TRIGGERED_ALERT_COLOR };
    case 'active':
      return { label: 'Active', color: colors.success };
    case 'acknowledged':
      return { label: 'Acknowledged', color: colors.textMuted };
    default:
      return { label: 'Inactive', color: colors.textMuted };
  }
};

const PriceAlertStatusIndicator = ({ status, colors }) => {
  if (status === 'triggered') {
    return (
      <BellFill
        size={14}
        color={TRIGGERED_ALERT_COLOR}
        style={{ flexShrink: 0 }}
        title="Triggered"
      />
    );
  }
  const indicatorColor = status === 'active' ? colors.success : colors.textMuted;
  const title = status === 'active' ? 'Active' : (status === 'acknowledged' ? 'Acknowledged' : 'Inactive');
  return (
    <CircleFill
      size={10}
      color={indicatorColor}
      style={{ flexShrink: 0 }}
      title={title}
    />
  );
};

/** Above = softer fill; below = bright white outline */
const ALERT_DIRECTION_PILL_COLOR = {
  above: '#e2e8f0',
  below: '#ffffff',
};

const pillBadge = (label, color, variant = 'outline') => (
  <span
    style={{
      padding: '2px 8px',
      borderRadius: 9999,
      fontSize: '0.7rem',
      fontWeight: 500,
      whiteSpace: 'nowrap',
      display: 'inline-block',
      backgroundColor: variant === 'filled' ? color : `${color}14`,
      color: variant === 'filled' ? '#111827' : color,
      border: `1px solid ${color}`,
    }}
  >
    {label}
  </span>
);

const TradeAlertsManager = ({
  renderAsChild = false,
  priceAlertsOnly = false,
  linkTickersToTrade = false,
}) => {
  const navigate = useNavigate();
  const [showModal, setShowModal] = useState(false);
  const [editingAlert, setEditingAlert] = useState(null);
  const [isCollapsed, setIsCollapsed] = useState(true);
  const [alertTypeFilter, setAlertTypeFilter] = useState(priceAlertsOnly ? 'price' : 'price');
  const [showFilterDropdown, setShowFilterDropdown] = useState(false);
  const [searchTicker, setSearchTicker] = useState('');
  const isMobile = useIsMobile(768, 1024);
  const { user } = useContext(AuthContext);
  const queryClient = useQueryClient();
  const tradingLockoutsQuery = useTradingLockoutsQuery(!!user?.token);
  const tradingLockouts = useMemo(() => tradingLockoutsQuery.data || [], [tradingLockoutsQuery.data]);
  const [formData, setFormData] = useState({
    ticker: '',
    alert_type: 'above',
    price_level: '',
    std_dev_level: '',
    timeframe: '',
    is_active: true
  });
  const { tradeAlerts, fetchTradeAlerts, activeChartId, updateChartSettings } = useContext(AppStateContext);
  const { showMessage } = useContext(BannerContext);
  const { accounts, selectedAccount, setSelectedAccount } = useContext(TradingContext);
  const canSelectTicker = canAccessTradeWorkspace(user, selectedAccount) !== false;
  const { createTradeAlert } = useCreateTradeAlert();
  const { updateTradeAlert } = useUpdateTradeAlert();
  const { deleteTradeAlert } = useDeleteTradeAlert();
  const { getTicker } = useGetTicker();
  const { colors } = useTheme();
  const [invalidTicker, setInvalidTicker] = useState(false);
  
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

  // Group alerts by type for better organization
  const groupedAlerts = useMemo(() => {
    const priceAlerts = (tradeAlerts || []).map(alert => ({
      ...alert,
      alertSource: 'price',
      ticker: alert.ticker,
      symbol: alert.ticker // For consistency
    }));
    
    const lockoutAlerts = (tradingLockouts || []).map(alert => ({
      ...alert,
      alertSource: 'lockout',
      ticker: alert.context?.positionSnapshot?.Symbol || 'N/A',
      symbol: alert.context?.positionSnapshot?.Symbol || 'N/A',
      alertType: alert.rule_type === 'max_loss_per_position' ? 'trade' : 'daily',
      alert_type: alert.rule_type === 'max_loss_per_position' ? 'lockout_trade' : 'lockout_daily',
      price_level: alert.threshold_value,
      thresholdAmount: alert.threshold_value,
      lossAmount: alert.actual_value,
      triggered_at: alert.starts_at,
      detectedAt: alert.starts_at,
      acknowledgedAt: alert.dismissed_at,
      archivedAt: alert.archived_at,
      accountId: alert.account_id,
      is_active: !alert.dismissed_at
    }));
    
    // Filter by ticker search
    const filterByTicker = (alerts) => {
      if (!searchTicker.trim()) return alerts;
      const searchLower = searchTicker.toLowerCase().trim();
      return alerts.filter(a => 
        a.ticker?.toLowerCase().includes(searchLower) || 
        a.symbol?.toLowerCase().includes(searchLower)
      );
    };
    
    // Filter by alert type
    let filteredPrice = filterByTicker(priceAlerts);
    let filteredLockouts = filterByTicker(lockoutAlerts);
    
    const typeFilter = priceAlertsOnly ? 'price' : alertTypeFilter;
    if (typeFilter === 'price') {
      filteredLockouts = [];
    } else if (typeFilter === 'lockout') {
      filteredPrice = [];
    }
    
    // Sort each group by triggered_at or created_at (most recent first)
    const sortAlerts = (alerts) => {
      return alerts.sort((a, b) => {
        const aTime = a.triggered_at || a.detectedAt || a.created_at || 0;
        const bTime = b.triggered_at || b.detectedAt || b.created_at || 0;
        return new Date(bTime) - new Date(aTime);
      });
    };
    
    // Group loss alerts by type
    const positionLossAlerts = sortAlerts(filteredLockouts.filter(a => a.alertType === 'trade'));
    const dailyLossAlerts = sortAlerts(filteredLockouts.filter(a => a.alertType === 'daily'));
    const sortedPriceAlerts = sortAlerts(filteredPrice);
    
    return {
      price: sortedPriceAlerts,
      positionLoss: positionLossAlerts,
      dailyLoss: dailyLossAlerts
    };
  }, [tradeAlerts, tradingLockouts, alertTypeFilter, priceAlertsOnly, searchTicker]);
  
  // For backward compatibility, also provide flat list
  const allAlerts = useMemo(() => {
    return [...groupedAlerts.price, ...groupedAlerts.positionLoss, ...groupedAlerts.dailyLoss];
  }, [groupedAlerts]);

  const displayAlerts = priceAlertsOnly ? groupedAlerts.price : allAlerts;

  const stdDevLevels = [
    { value: 'std_dev_1_upper', label: '1.0 Upper' },
    { value: 'std_dev_1_lower', label: '1.0 Lower' },
    { value: 'std_dev_1_5_upper', label: '1.5 Upper' },
    { value: 'std_dev_1_5_lower', label: '1.5 Lower' },
    { value: 'std_dev_2_upper', label: '2.0 Upper' },
    { value: 'std_dev_2_lower', label: '2.0 Lower' }
  ];
  const stdDevTimeframes = [
    '5min', '15min', '30min', '1hour', '4hour', 'daily'
  ];

  const [alertMode, setAlertMode] = useState('price'); // 'price' or 'stddev'

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Check if user is authenticated
    if (!user?.token) {
      showMessage('You must be logged in to create alerts', 'danger');
      return;
    }

    // Validate ticker symbol via TradeStation API (price alerts only)
    if (alertMode === 'price' && formData.ticker) {
      try {
        const tickerDetails = await getTicker(formData.ticker);
        if (!tickerDetails || tickerDetails.Errors?.length || !tickerDetails.Symbols?.[0]) {
          setInvalidTicker(true);
          return;
        }
        setInvalidTicker(false);
      } catch (_) {
        setInvalidTicker(true);
        return;
      }
    }

    let payload = { ...formData };
    
    // For std dev alerts, don't send price_level (backend will calculate it)
    if (alertMode === 'stddev') {
      delete payload.price_level;
    } else {
      // For price alerts, don't send std dev fields
      delete payload.std_dev_level;
      delete payload.timeframe;
    }

    try {
      const response = editingAlert 
        ? await updateTradeAlert(user.token, editingAlert.id, payload)
        : await createTradeAlert(user.token, payload);
        
      if (response && !response.error) {
        // Track alert creation (not edits)
        if (!editingAlert) {
          analyticsService.trackTradingEvent('alert_created', {
            ticker: payload.ticker,
            alert_type: payload.alert_type,
            alert_mode: alertMode,
            price_level: payload.price_level,
            std_dev_level: payload.std_dev_level || null,
            timeframe: payload.timeframe || null
          });
        }
        
        showMessage(editingAlert ? 'Alert updated successfully!' : 'Alert created successfully!', 'success');
        setShowModal(false);
        resetForm();
        fetchTradeAlerts(); // Refresh from context
        queryClient.invalidateQueries({ queryKey: queryKeys.tradingLockouts });
      }
    } catch (error) {
      showMessage('Failed to save alert', 'danger');
      console.error('Error saving alert:', error);
    }
  };

  const handleDelete = async (alertId) => {
    if (!window.confirm('Are you sure you want to delete this alert?')) {
      return;
    }

    try {
      const response = await deleteTradeAlert(user.token, alertId);
      if (response && !response.error) {
        showMessage('Alert deleted successfully!', 'success');
        fetchTradeAlerts(); // Refresh from context
      }
    } catch (error) {
      showMessage('Failed to delete alert', 'danger');
      console.error('Error deleting alert:', error);
    }
  };

  const handleReactivate = async (alert) => {
    try {
      // Re-enable the alert by setting is_active to true and clearing triggered_at
      const response = await updateTradeAlert(user.token, alert.id, {
        ...alert,
        is_active: true
      });
      if (response && !response.error) {
        showMessage('Alert re-enabled successfully!', 'success');
        fetchTradeAlerts(); // Refresh from context
      }
    } catch (error) {
      showMessage('Failed to re-enable alert', 'danger');
      console.error('Error re-enabling alert:', error);
    }
  };

  const handleAcknowledge = async (alertId) => {
    if (!user?.token) return;
    try {
      const res = await fetch(`${BACKEND_URL}/trade_alerts/${alertId}/acknowledge`, {
        method: 'PATCH',
        headers: {
          Authorization: `Bearer ${user.token}`,
          'Content-Type': 'application/json',
        },
      });
      if (res.ok) {
        showMessage('Alert acknowledged', 'success');
        fetchTradeAlerts();
      } else {
        showMessage('Failed to acknowledge alert', 'danger');
      }
    } catch (error) {
      showMessage('Failed to acknowledge alert', 'danger');
      console.error('Error acknowledging alert:', error);
    }
  };

  const handleEdit = (alert) => {
    setEditingAlert(alert);
    if (alert.std_dev_level && alert.timeframe) {
      setAlertMode('stddev');
      setFormData({
        ticker: alert.ticker,
        alert_type: alert.alert_type,
        std_dev_level: alert.std_dev_level,
        timeframe: alert.timeframe,
        price_level: '',
        is_active: alert.is_active
      });
    } else {
      setAlertMode('price');
      setFormData({
        ticker: alert.ticker,
        alert_type: alert.alert_type,
        price_level: alert.price_level.toString(),
        std_dev_level: '',
        timeframe: '',
        is_active: alert.is_active
      });
    }
    setShowModal(true);
  };

  const resetForm = () => {
    setFormData({
      ticker: '',
      alert_type: 'above',
      price_level: '',
      std_dev_level: '',
      timeframe: '',
      is_active: true
    });
    setAlertMode('price');
    setEditingAlert(null);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    resetForm();
  };

  const handlePriceAlertTickerClick = useCallback((alert) => {
    if (!alert.ticker) return;
    if (!canSelectTicker) return;
    if (linkTickersToTrade && navigateToTradeWithSymbol(navigate, alert.ticker, user, selectedAccount)) {
      return;
    }
    if (activeChartId) {
      updateChartSettings(activeChartId, { ticker: alert.ticker });
    }
    window.dispatchEvent(new CustomEvent('alerts:tickerClicked'));
  }, [canSelectTicker, linkTickersToTrade, navigate, user, selectedAccount, activeChartId, updateChartSettings]);

  const mobileActionBtnStyle = {
    width: 40,
    height: 40,
    minWidth: 40,
    minHeight: 40,
    border: 'none',
    background: 'none',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 8,
    flexShrink: 0,
  };

  const renderPriceAlertCard = useCallback((alert, status) => {
    const { label: statusLabel, color: statusColor } = getPriceStatusPresentation(status, colors);
    const directionColor = '#ffffff';
    const tickerIsLink = !!alert.ticker && canSelectTicker && (linkTickersToTrade || activeChartId);
    const isTriggered = status === 'triggered';
    const isAcknowledged = status === 'acknowledged';

    return (
      <div
        key={`price-card-${alert.id}`}
        style={{
          backgroundColor: colors.bgCard,
          border: `1px solid ${colors.border}`,
          borderRadius: 10,
          padding: '12px 14px',
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            gap: 12,
            marginBottom: 10,
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0, flex: 1 }}>
            <PriceAlertStatusIndicator status={status} colors={colors} />
            {tickerIsLink ? (
              <button
                type="button"
                onClick={() => handlePriceAlertTickerClick(alert)}
                title={linkTickersToTrade ? `Open ${alert.ticker} on Trade` : `Show ${alert.ticker} on chart`}
                style={{
                  fontWeight: 600,
                  fontSize: '1rem',
                  color: colors.primary,
                  padding: '4px 8px',
                  background: `${colors.primary}18`,
                  borderRadius: 6,
                  border: 'none',
                  cursor: 'pointer',
                  flexShrink: 0,
                }}
              >
                {alert.ticker}
              </button>
            ) : (
              <span style={{ fontWeight: 600, fontSize: '1rem', color: colors.text }}>
                {alert.ticker || 'N/A'}
              </span>
            )}
          </div>
          {pillBadge(statusLabel, statusColor)}
        </div>

        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: 10,
            marginBottom: isTriggered ? 8 : 0,
          }}
        >
          {pillBadge(
            alert.alert_type,
            ALERT_DIRECTION_PILL_COLOR[alert.alert_type] || ALERT_DIRECTION_PILL_COLOR.below,
            alert.alert_type === 'above' ? 'filled' : 'outline'
          )}
          <div>
            {alert.std_dev_level && (
              <div style={{ fontSize: '0.72rem', color: colors.textMuted, marginBottom: 2 }}>
                {formatStdDevLabel(alert.std_dev_level)}
              </div>
            )}
            <strong style={{ fontSize: '0.95rem', color: colors.text }}>
              ${parseFloat(alert.price_level).toFixed(2)}
            </strong>
          </div>
        </div>

        {(isTriggered || isAcknowledged) && alert.triggered_at && (
          <div style={{ fontSize: '0.72rem', color: colors.textMuted, marginBottom: 10 }}>
            Triggered {new Date(alert.triggered_at).toLocaleString()}
          </div>
        )}

        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            gap: 4,
            marginTop: 10,
            paddingTop: 10,
            borderTop: `1px solid ${colors.borderLight}`,
          }}
        >
          {isTriggered && (
            <button
              type="button"
              aria-label="Acknowledge alert"
              onClick={() => handleAcknowledge(alert.id)}
              style={{
                border: 'none',
                background: 'none',
                color: colors.textMuted,
                fontSize: '0.78rem',
                padding: '8px 10px',
                minHeight: 40,
                cursor: 'pointer',
                textDecoration: 'underline',
                textUnderlineOffset: 2,
              }}
            >
              Ack
            </button>
          )}
          {alert.triggered_at && !alert.is_active && (
            <Button
              variant="link"
              size="sm"
              style={{
                color: colors.success,
                fontSize: '0.85rem',
                textDecoration: 'none',
                minHeight: 40,
                padding: '8px 12px',
              }}
              onClick={() => handleReactivate(alert)}
            >
              Re-Enable
            </Button>
          )}
          <button
            type="button"
            aria-label="Edit alert"
            style={{ ...mobileActionBtnStyle, color: colors.info }}
            onClick={() => handleEdit(alert)}
          >
            <Pencil size={18} />
          </button>
          <button
            type="button"
            aria-label="Delete alert"
            style={{ ...mobileActionBtnStyle, color: colors.danger }}
            onClick={() => handleDelete(alert.id)}
          >
            <Trash size={18} />
          </button>
        </div>
      </div>
    );
  }, [
    colors,
    linkTickersToTrade,
    canSelectTicker,
    activeChartId,
    handlePriceAlertTickerClick,
    handleAcknowledge,
    handleReactivate,
    handleEdit,
    handleDelete,
  ]);

  // Render a single alert row (extracted for reuse)
  const renderAlertRow = useCallback((alert, alertSource, isTriggered, isAcknowledged, isActive, priceStatus) => {
    const isPriceAlert = alertSource === 'price';
    const isLossLimitAlert = alertSource === 'lockout';
    const pricePresentation = isPriceAlert && priceStatus
      ? getPriceStatusPresentation(priceStatus, colors)
      : null;

    const directionColor = isPriceAlert
      ? '#ffffff'
      : (alert.alertType === 'trade' ? (colors.purple || colors.primaryLight) : colors.danger);

    const typeLabel = isPriceAlert
      ? alert.alert_type
      : (alert.alertType === 'trade' ? 'Max Position Loss' : 'Daily Loss');

    const statusColor = (() => {
      if (isPriceAlert && pricePresentation) return pricePresentation.color;
      if (isTriggered) return TRIGGERED_ALERT_COLOR;
      if (isActive) return colors.success;
      return colors.textMuted;
    })();

    const statusLabel = (() => {
      if (isPriceAlert && pricePresentation) return pricePresentation.label;
      if (isTriggered) return 'Triggered';
      if (isActive) return 'Active';
      return isAcknowledged ? 'Acknowledged' : 'Inactive';
    })();

    const triggeredTs = alert.triggered_at || alert.detectedAt;

    return (
      <tr key={`${alertSource}-${alert.id}`}>
        <td style={{ padding: '8px 10px', whiteSpace: 'nowrap' }}>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              cursor: alert.ticker ? 'pointer' : 'default',
            }}
            onClick={() => {
              if (isPriceAlert && alert.ticker) {
                handlePriceAlertTickerClick(alert);
              } else if (isLossLimitAlert && alert.ticker && alert.accountId) {
                const targetAccount = accounts?.find(acc => acc.AccountID === alert.accountId);
                if (targetAccount) {
                  setSelectedAccount(targetAccount);
                  if (supportsMarketData(user, targetAccount) && activeChartId) {
                    updateChartSettings(activeChartId, { ticker: alert.ticker });
                  }
                  showMessage(
                    `Switched to ${getAccountDisplayLabel(targetAccount, user?.accountSettings || user?.accountDefaults)} for ${alert.ticker}`,
                    'info'
                  );
                  if (supportsMarketData(user, targetAccount)) {
                    window.dispatchEvent(new CustomEvent('alerts:tickerClicked'));
                  }
                } else {
                  showMessage(`Account ${alert.accountId} not found`, 'warning');
                }
              }
            }}
          >
            {isPriceAlert && priceStatus ? (
              <PriceAlertStatusIndicator status={priceStatus} colors={colors} />
            ) : (
              <CircleFill
                size={8}
                color={isTriggered ? TRIGGERED_ALERT_COLOR : (isActive ? colors.success : colors.textMuted)}
                title={statusLabel}
              />
            )}
            <span style={{ fontWeight: 600, fontSize: '0.8rem', color: alert.ticker ? colors.primary : colors.text }}>
              {alert.ticker || 'N/A'}
            </span>
          </div>
          {isLossLimitAlert && alert.accountId && (
            <div style={{ fontSize: '0.65rem', color: colors.textMuted, marginTop: 2, fontFamily: 'monospace' }}>
              {getAccountDisplayLabel(alert.accountId, user?.accountSettings || user?.accountDefaults)}
            </div>
          )}
        </td>

        <td style={{ padding: '8px 10px', whiteSpace: 'nowrap' }}>
          {isPriceAlert
            ? pillBadge(
                typeLabel,
                ALERT_DIRECTION_PILL_COLOR[alert.alert_type] || ALERT_DIRECTION_PILL_COLOR.below,
                alert.alert_type === 'above' ? 'filled' : 'outline'
              )
            : pillBadge(typeLabel, directionColor)}
        </td>

        <td style={{ padding: '8px 10px', whiteSpace: 'nowrap' }}>
          {isPriceAlert ? (
            <>
              {alert.std_dev_level && (
                <div style={{ fontSize: '0.65rem', color: colors.textMuted, marginBottom: 1 }}>
                  {formatStdDevLabel(alert.std_dev_level)}
                </div>
              )}
              <span style={{ fontWeight: 600, fontSize: '0.8rem', color: colors.text }}>
                ${parseFloat(alert.price_level).toFixed(2)}
              </span>
            </>
          ) : (
            <div>
              <span style={{ fontSize: '0.8rem', color: colors.text }}>
                ${parseFloat(alert.thresholdAmount || alert.price_level).toFixed(2)}
              </span>
              {isTriggered && (
                <span style={{ fontSize: '0.75rem', color: colors.chartDown, marginLeft: 8, fontWeight: 600 }}>
                  Loss: ${parseFloat(alert.lossAmount || 0).toFixed(2)}
                </span>
              )}
            </div>
          )}
        </td>

        <td style={{ padding: '8px 10px', whiteSpace: 'nowrap' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
            {pillBadge(statusLabel, statusColor)}
            {(isTriggered || (isPriceAlert && priceStatus === 'acknowledged')) && triggeredTs && (
              <span style={{ fontSize: '0.65rem', color: colors.textMuted }}>
                {new Date(triggeredTs).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}
              </span>
            )}
          </div>
        </td>

        <td style={{ padding: '8px 10px', whiteSpace: 'nowrap', textAlign: 'right' }}>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 4, alignItems: 'center' }}>
            {isPriceAlert && priceStatus === 'triggered' && (
              <button
                type="button"
                style={{ border: 'none', background: 'none', color: colors.textMuted, fontSize: '0.75rem', padding: '4px 6px', cursor: 'pointer', textDecoration: 'underline', textUnderlineOffset: 2 }}
                onClick={() => handleAcknowledge(alert.id)}
              >
                Ack
              </button>
            )}
            {isPriceAlert && alert.triggered_at && !alert.is_active && (
              <button
                type="button"
                style={{ border: 'none', background: 'none', color: colors.success, fontSize: '0.75rem', padding: '4px 6px', cursor: 'pointer' }}
                onClick={() => handleReactivate(alert)}
              >
                Re-Enable
              </button>
            )}
            {isPriceAlert && (
              <>
                <button
                  type="button"
                  aria-label="Edit alert"
                  style={{ border: 'none', background: 'none', color: colors.info, padding: 4, cursor: 'pointer', display: 'inline-flex', alignItems: 'center' }}
                  onClick={() => handleEdit(alert)}
                >
                  <Pencil size={14} />
                </button>
                <button
                  type="button"
                  aria-label="Delete alert"
                  style={{ border: 'none', background: 'none', color: colors.danger, padding: 4, cursor: 'pointer', display: 'inline-flex', alignItems: 'center' }}
                  onClick={() => handleDelete(alert.id)}
                >
                  <Trash size={14} />
                </button>
              </>
            )}
            {isLossLimitAlert && !alert.archivedAt && (
              <button
                type="button"
                aria-label="Archive alert"
                style={{ border: 'none', background: 'none', color: colors.textMuted, padding: 4, cursor: 'pointer', display: 'inline-flex', alignItems: 'center' }}
                onClick={async () => {
                  try {
                    const res = await fetch(`${BACKEND_URL}/enforcement/alerts/${alert.id}/archive`, {
                      method: 'PATCH',
                      headers: { 'Authorization': `Bearer ${user.token}`, 'Content-Type': 'application/json' }
                    });
                    const json = await res.json();
                    if (res.ok && json.success) {
                      showMessage('Alert archived successfully', 'success');
                      queryClient.invalidateQueries({ queryKey: queryKeys.tradingLockouts });
                    } else {
                      showMessage('Failed to archive alert', 'danger');
                    }
                  } catch (err) {
                    console.error('[TradeAlertsManager] Error archiving alert:', err);
                    showMessage('Failed to archive alert', 'danger');
                  }
                }}
              >
                <Archive size={14} />
              </button>
            )}
          </div>
        </td>
      </tr>
    );
  }, [colors, activeChartId, updateChartSettings, handleAcknowledge, handleReactivate, handleEdit, handleDelete, BACKEND_URL, user?.token, showMessage, queryClient, accounts, setSelectedAccount, handlePriceAlertTickerClick]);

  const priceAlertsInView = useMemo(
    () => displayAlerts.filter((a) => a.alertSource === 'price'),
    [displayAlerts]
  );
  const lossAlertsInView = useMemo(
    () => displayAlerts.filter((a) => a.alertSource === 'lockout'),
    [displayAlerts]
  );
  const showMobilePriceCards = isMobile && priceAlertsInView.length > 0;

  const tableContent = (
    <div>
      {!user?.token && (
        <Alert variant="warning">
          You must be logged in to create and manage alerts. Please go to the Login page and sign in with your email and password.
        </Alert>
      )}

      {/* Filter and Search Controls */}
      {user?.token && (
        <div style={{
          padding: '16px 20px',
          borderBottom: `1px solid ${colors.border}`,
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          flexWrap: 'nowrap',
        }}>
          {/* Ticker Search */}
          <div style={{ minWidth: '80px', flex: '1', maxWidth: '150px' }}>
            <Form.Control
              type="text"
              placeholder="Symbol..."
              size="sm"
              value={searchTicker}
              onChange={(e) => setSearchTicker(e.target.value)}
              style={{
                backgroundColor: colors.bgInput,
                borderColor: colors.primary,
                color: colors.text,
                fontSize: '0.8rem'
              }}
            />
          </div>
          
          {/* Alert Type Dropdown */}
          {!priceAlertsOnly && (
          <Dropdown 
            show={showFilterDropdown} 
            onToggle={setShowFilterDropdown}
            style={{ minWidth: isMobile ? '140px' : '150px', flexShrink: 0 }}
          >
            <Dropdown.Toggle
              as={Button}
              variant="outline-custom"
              onClick={() => setShowFilterDropdown(!showFilterDropdown)}
              style={{
                backgroundColor: colors.bgInput,
                border: `1px solid ${colors.borderLight}`,
                color: colors.text,
                padding: isMobile ? '8px 12px' : '6px 12px',
                height: isMobile ? 40 : 32,
                minHeight: isMobile ? 40 : 32,
                lineHeight: isMobile ? '22px' : '18px',
                fontSize: isMobile ? '0.9rem' : '0.85rem',
                fontWeight: 400,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                width: '100%',
                textAlign: 'left',
                boxShadow: 'none',
                outline: 'none',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                borderRadius: '6px'
              }}
            >
              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {alertTypeFilter === 'all' ? 'All Alerts' : alertTypeFilter === 'price' ? 'Price Alerts' : 'Loss Limit Alerts'}
              </span>
              <ChevronDown size={14} style={{ marginLeft: '8px', flexShrink: 0 }} />
            </Dropdown.Toggle>
            <Dropdown.Menu
              style={{
                backgroundColor: colors.bgSecondary,
                border: `1px solid ${colors.borderLight}`,
                borderRadius: '8px',
                padding: '4px 0',
                marginTop: '4px',
                minWidth: isMobile ? '180px' : '160px',
                fontSize: isMobile ? '0.95rem' : '0.85rem',
                zIndex: 9999
              }}
            >
              {[
                { value: 'all', label: 'All Alerts' },
                { value: 'price', label: 'Price Alerts' },
                { value: 'lockout', label: 'Trading Lockouts' }
              ].map(option => (
                <Dropdown.Item
                  key={option.value}
                  onClick={() => {
                    setAlertTypeFilter(option.value);
                    setShowFilterDropdown(false);
                  }}
                  style={{
                    color: option.value === alertTypeFilter ? colors.primary : colors.text,
                    backgroundColor: option.value === alertTypeFilter ? `${colors.primary}1A` : 'transparent',
                    padding: isMobile ? '10px 16px' : '8px 14px',
                    fontWeight: option.value === alertTypeFilter ? 600 : 400,
                    cursor: 'pointer',
                    whiteSpace: 'nowrap'
                  }}
                  onMouseEnter={(e) => {
                    if (option.value !== alertTypeFilter) {
                      e.target.style.backgroundColor = `${colors.primary}0D`;
                      e.target.style.color = colors.primary;
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (option.value !== alertTypeFilter) {
                      e.target.style.backgroundColor = 'transparent';
                      e.target.style.color = colors.text;
                    }
                  }}
                >
                  {option.label}
                </Dropdown.Item>
              ))}
            </Dropdown.Menu>
          </Dropdown>
          )}
          
          {/* Add Alert Button */}
          <Button
            size="sm"
            onClick={() => setShowModal(true)}
            style={{
              backgroundColor: colors.primary,
              borderColor: colors.primary,
              color: '#fff',
              fontSize: '0.8rem',
              padding: '6px 12px',
              fontWeight: 500,
              whiteSpace: 'nowrap',
              flexShrink: 0
            }}
          >
            + Alert
          </Button>
        </div>
      )}
      
      {displayAlerts.length === 0 ? (
        <p className="text-center py-3" style={{ fontSize: '0.8rem', color: colors.textMuted }}>
          {searchTicker || (!priceAlertsOnly && alertTypeFilter !== 'all')
            ? 'No alerts match your filters.' 
            : priceAlertsOnly
              ? 'No price alerts yet. Create one from a chart, or tap "+ Alert" after you upgrade to Trade.'
              : 'No alerts configured. Click "+ Alert" to create your first alert.'}
        </p>
      ) : showMobilePriceCards ? (
        <>
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: 10,
              padding: '12px',
            }}
          >
            {priceAlertsInView.map((alert) => {
              const status = getPriceAlertStatus(alert);
              return renderPriceAlertCard(alert, status);
            })}
          </div>
          {lossAlertsInView.length > 0 && (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', fontSize: '0.85rem', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ borderBottom: `1px solid ${colors.border}` }}>
                    {['Ticker', 'Alert Type', 'Threshold', 'Status', ''].map((h) => (
                      <th key={h} style={{ padding: '8px 10px', textAlign: h === '' ? 'right' : 'left', color: colors.textMuted, fontWeight: 500, fontSize: '0.8rem', whiteSpace: 'nowrap' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {lossAlertsInView.map((alert, i) => {
                    const isTriggered = !!alert.detectedAt;
                    const isAcknowledged = !!alert.acknowledgedAt;
                    const isActive = !isAcknowledged;
                    const row = renderAlertRow(alert, 'lockout', isTriggered, isAcknowledged, isActive, null);
                    return React.cloneElement(row, {
                      style: { borderBottom: i < lossAlertsInView.length - 1 ? `1px solid ${colors.border}` : 'none' }
                    });
                  })}
                </tbody>
              </table>
            </div>
          )}
        </>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', fontSize: '0.85rem', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ borderBottom: `1px solid ${colors.border}` }}>
                {['Ticker', 'Type', 'Price', 'Status', ''].map((h) => (
                  <th key={h} style={{ padding: '8px 10px', textAlign: h === '' ? 'right' : 'left', color: colors.textMuted, fontWeight: 500, fontSize: '0.8rem', whiteSpace: 'nowrap' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {displayAlerts.map((alert, i) => {
                const isPriceAlert = alert.alertSource === 'price';
                const priceStatus = isPriceAlert ? getPriceAlertStatus(alert) : null;
                const isTriggered = isPriceAlert ? priceStatus === 'triggered' : (!!alert.triggered_at || !!alert.detectedAt);
                const isAcknowledged = isPriceAlert ? priceStatus === 'acknowledged' : !!alert.acknowledgedAt;
                const isActive = isPriceAlert ? priceStatus === 'active' : !isAcknowledged;
                const row = renderAlertRow(alert, isPriceAlert ? 'price' : 'lockout', isTriggered, isAcknowledged, isActive, priceStatus);
                return React.cloneElement(row, {
                  style: { borderBottom: i < displayAlerts.length - 1 ? `1px solid ${colors.border}` : 'none' }
                });
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );


  const labelStyle = {
    color: colors.text,
    fontSize: '0.875rem',
    marginBottom: '0.25rem',
  };

  // Modal component (shared between renderAsChild and standalone modes)
  const alertModal = (
    <Modal 
      show={showModal} 
      onHide={handleCloseModal}
      centered
    >
      <Modal.Header 
        closeButton 
        style={{ 
          backgroundColor: colors.bgSecondary, 
          borderBottom: `1px solid ${colors.border}`,
          color: colors.text 
        }}
        closeVariant="white"
      >
        <Modal.Title style={{ color: colors.text }}>
          {editingAlert ? 'Edit Alert' : 'Create New Alert'}
        </Modal.Title>
      </Modal.Header>
      <Form onSubmit={handleSubmit}>
        <Modal.Body style={{ backgroundColor: colors.bgCard }}>
          <Form.Group className="mb-3">
            <Form.Label style={labelStyle}>Ticker Symbol</Form.Label>
            <TickerComboBox
              value={formData.ticker}
              onChange={(value) => {
                setInvalidTicker(false);
                setFormData({...formData, ticker: value});
              }}
              placeholder="e.g., MNQ"
              required
              isInvalid={invalidTicker}
            />
            {invalidTicker && (
              <Form.Text style={{ color: colors.danger, fontSize: '0.8rem' }}>
                Invalid ticker symbol
              </Form.Text>
            )}
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label style={labelStyle}>Alert Mode</Form.Label>
            <Form.Select
              value={alertMode}
              onChange={e => setAlertMode(e.target.value)}
              className="themed-input"
            >
              <option value="price">Price Level</option>
              <option value="stddev">Standard Deviation</option>
            </Form.Select>
          </Form.Group>
          {alertMode === 'price' && (
            <>
              <Form.Group className="mb-3">
                <Form.Label style={labelStyle}>Alert Type</Form.Label>
                <Form.Select
                  value={formData.alert_type}
                  onChange={(e) => setFormData({...formData, alert_type: e.target.value})}
                  className="themed-input"
                >
                  <option value="above">Above Price</option>
                  <option value="below">Below Price</option>
                </Form.Select>
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label style={labelStyle}>Price Level</Form.Label>
                <Form.Control
                  type="number"
                  step="0.01"
                  placeholder="e.g., 16500.50"
                  value={formData.price_level}
                  onChange={(e) => setFormData({...formData, price_level: e.target.value})}
                  required
                  className="themed-input"
                />
              </Form.Group>
            </>
          )}
          {alertMode === 'stddev' && (
            <>
              <Form.Group className="mb-3">
                <Form.Label style={labelStyle}>Alert Type</Form.Label>
                <Form.Select
                  value={formData.alert_type}
                  onChange={(e) => setFormData({...formData, alert_type: e.target.value})}
                  className="themed-input"
                >
                  <option value="above">Above Std Dev</option>
                  <option value="below">Below Std Dev</option>
                </Form.Select>
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label style={labelStyle}>Std Dev Level</Form.Label>
                <Form.Select
                  value={formData.std_dev_level}
                  onChange={e => setFormData({...formData, std_dev_level: e.target.value})}
                  required
                  className="themed-input"
                >
                  <option value="">Select Level</option>
                  {stdDevLevels.map((level) => (
                    <option key={level.value} value={level.value}>
                      {level.label}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label style={labelStyle}>Timeframe</Form.Label>
                <Form.Select
                  value={formData.timeframe}
                  onChange={e => setFormData({...formData, timeframe: e.target.value})}
                  required
                  className="themed-input"
                >
                  <option value="">Select Timeframe</option>
                  {stdDevTimeframes.map(tf => (
                    <option key={tf} value={tf}>{tf}</option>
                  ))}
                </Form.Select>
              </Form.Group>
            </>
          )}
          <Form.Group className="mb-3">
            <Form.Check
              type="checkbox"
              label="Active"
              checked={formData.is_active}
              onChange={(e) => setFormData({...formData, is_active: e.target.checked})}
              style={{ color: colors.text }}
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer style={{ backgroundColor: colors.bgSecondary, borderTop: `1px solid ${colors.border}` }}>
          <Button 
            variant="secondary" 
            onClick={handleCloseModal}
            style={{ 
              backgroundColor: colors.bgInput, 
              borderColor: colors.border, 
              color: colors.text 
            }}
          >
            Cancel
          </Button>
          <Button 
            type="submit"
            style={{ 
              backgroundColor: colors.primary, 
              borderColor: colors.primary, 
              color: '#fff' 
            }}
          >
            {editingAlert ? 'Update Alert' : 'Create Alert'}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );

  if (renderAsChild) {
    return (
      <div>
        {tableContent}
        {alertModal}
      </div>
    );
  }

  return (
    <Card className="mb-3" style={{ backgroundColor: colors.bgCard, border: `1px solid ${colors.border}` }}>
      <Card.Header 
        className="d-flex justify-content-between align-items-center"
        style={{ backgroundColor: colors.bgSecondary, borderBottom: `1px solid ${colors.border}` }}
      >
        <div className="d-flex align-items-center">
          <h5 className="mb-0 me-2" style={{ color: colors.text }}>Alerts</h5>
          <Button 
            variant="link" 
            className="p-0"
            style={{ color: colors.text }}
            onClick={() => setIsCollapsed(!isCollapsed)}
          >
            {isCollapsed ? <ChevronRight /> : <ChevronDown />}
          </Button>
        </div>
      </Card.Header>
      
      {!isCollapsed && (
        <Card.Body style={{ padding: 0, backgroundColor: colors.bgPrimary }}>
          {tableContent}
        </Card.Body>
      )}

      {alertModal}
    </Card>
  );
};

export default TradeAlertsManager; 